import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Trash2, AlertTriangle, CheckCircle2, Loader2, Cookie } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiFetch, API } from "@/lib/api";
import SidebarLayout from "@/components/SidebarLayout";
import SEO from "@/components/SEO";

/**
 * /settings/data — one-page GDPR self-service.
 *
 * - Request ZIP export (async job, polling)
 * - Hard delete account (3-step confirm)
 * - Reset cookie preferences
 */
export default function SettingsData() {
    const { user, refresh, logout } = useAuth();
    const [job, setJob] = useState(null);
    const [pollTimer, setPollTimer] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);

    useEffect(() => () => pollTimer && clearInterval(pollTimer), [pollTimer]);

    const requestExport = async () => {
        try {
            const res = await apiFetch("/user/data-export", { method: "POST" });
            setJob(res);
            if (res.status !== "completed") pollJob(res.job_id);
            toast.success("Export requested — you'll be notified when ready.");
        } catch (e) {
            toast.error(e.message || "Export request failed");
        }
    };

    const pollJob = (jobId) => {
        if (pollTimer) clearInterval(pollTimer);
        const t = setInterval(async () => {
            try {
                const res = await apiFetch(`/user/data-export/${jobId}`);
                setJob(res);
                if (res.status === "completed" || res.status === "failed") {
                    clearInterval(t);
                    setPollTimer(null);
                }
            } catch {}
        }, 2000);
        setPollTimer(t);
    };

    const resetCookies = () => {
        window.localStorage.removeItem("glms_cookie_consent");
        toast.success("Cookie preferences reset — banner will appear on next reload.");
    };

    return (
        <SidebarLayout>
            <SEO title="Data & Privacy" description="Export your data or delete your Global Lead Suite account." noindex />
            <div className="mx-auto max-w-3xl px-6 py-10" data-testid="settings-data-page">
                <div className="mb-10">
                    <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                        Account
                    </p>
                    <h1 className="text-3xl font-semibold mt-1">Data & Privacy</h1>
                    <p className="text-sm text-text-secondary mt-2 max-w-xl">
                        Everything you need to exercise your GDPR rights. See{" "}
                        <Link
                            to="/legal/gdpr"
                            className="text-[var(--color-secondary)] hover:underline"
                        >
                            /legal/gdpr
                        </Link>{" "}
                        for the full policy.
                    </p>
                </div>

                <section
                    data-testid="section-data-export"
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 mb-6"
                >
                    <div className="flex items-start gap-3 mb-4">
                        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-2">
                            <Download className="h-4 w-4 text-[var(--color-secondary)]" />
                        </span>
                        <div>
                            <h2 className="text-lg font-semibold">Download my data</h2>
                            <p className="text-xs text-text-secondary mt-1 max-w-lg">
                                We package every record we hold under your user id into a ZIP with one JSON per collection. Link valid 48 hours.
                            </p>
                        </div>
                    </div>

                    {job ? (
                        <JobPanel job={job} />
                    ) : (
                        <button
                            data-testid="data-export-btn"
                            onClick={requestExport}
                            className="rounded-full border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-4 py-2 text-sm hover:border-[var(--color-primary)]/50 transition-colors"
                        >
                            Request export
                        </button>
                    )}
                </section>

                <section
                    data-testid="section-cookie-prefs"
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 mb-6"
                >
                    <div className="flex items-start gap-3 mb-4">
                        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-2">
                            <Cookie className="h-4 w-4 text-[var(--color-secondary)]" />
                        </span>
                        <div>
                            <h2 className="text-lg font-semibold">Cookie preferences</h2>
                            <p className="text-xs text-text-secondary mt-1 max-w-lg">
                                Reset the cookie consent banner. It will reappear on the next page load so you can update your choice.
                            </p>
                        </div>
                    </div>
                    <button
                        data-testid="reset-cookies-btn"
                        onClick={resetCookies}
                        className="text-xs text-text-secondary hover:text-text-main"
                    >
                        Reset cookie preferences
                    </button>
                </section>

                <section
                    data-testid="section-delete-account"
                    className="rounded-2xl border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/5 p-6"
                >
                    <div className="flex items-start gap-3 mb-4">
                        <span className="rounded-full border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 p-2">
                            <AlertTriangle className="h-4 w-4 text-[var(--color-alert)]" />
                        </span>
                        <div>
                            <h2 className="text-lg font-semibold text-[var(--color-alert)]">
                                Delete my account
                            </h2>
                            <p className="text-xs text-text-secondary mt-1 max-w-lg">
                                Instantaneous hard delete — cancels any active Stripe subscription, cascades across every user-owned record, no grace period. This action cannot be undone.
                            </p>
                        </div>
                    </div>
                    <button
                        data-testid="delete-account-btn"
                        onClick={() => setDeleteOpen(true)}
                        className="rounded-full border border-[var(--color-alert)]/50 bg-[var(--color-alert)]/10 px-4 py-2 text-sm text-[var(--color-alert)] hover:bg-[var(--color-alert)]/20 transition-colors"
                    >
                        <Trash2 className="h-3.5 w-3.5 inline mr-1.5" />
                        Delete account
                    </button>
                </section>
            </div>

            {deleteOpen && (
                <DeleteConfirmModal
                    userEmail={user?.email}
                    onClose={() => setDeleteOpen(false)}
                    onDeleted={async () => {
                        toast.success("Your account has been deleted");
                        await logout();
                    }}
                />
            )}
        </SidebarLayout>
    );
}

function JobPanel({ job }) {
    const isDone = job.status === "completed";
    const isFailed = job.status === "failed";
    return (
        <div
            data-testid="data-export-status"
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-4 flex items-center gap-3"
        >
            {isDone ? (
                <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" />
            ) : isFailed ? (
                <AlertTriangle className="h-4 w-4 text-[var(--color-alert)]" />
            ) : (
                <Loader2 className="h-4 w-4 animate-spin text-[var(--color-secondary)]" />
            )}
            <div className="flex-1">
                <div className="text-sm">
                    {isDone
                        ? "Your export is ready."
                        : isFailed
                        ? "Export failed. Please try again later."
                        : `Preparing your export (${job.status})…`}
                </div>
                {isDone && job.expires_at && (
                    <div className="text-[11px] text-text-secondary">
                        Link expires: {new Date(job.expires_at).toLocaleString()}
                    </div>
                )}
            </div>
            {isDone && job.download_url && (
                <a
                    data-testid="data-export-download"
                    href={job.download_url}
                    className="text-xs rounded-full bg-white hover:bg-[#EDEDED] px-4 py-2 text-black font-medium hover:opacity-90"
                >
                    Download ZIP
                </a>
            )}
        </div>
    );
}

function DeleteConfirmModal({ userEmail, onClose, onDeleted }) {
    const [step, setStep] = useState(1);
    const [acknowledged, setAcknowledged] = useState(false);
    const [typedDelete, setTypedDelete] = useState("");
    const [emailConfirm, setEmailConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    const canStep2 = acknowledged;
    const canStep3 = typedDelete === "DELETE";
    const canSubmit =
        emailConfirm.trim().toLowerCase() === (userEmail || "").toLowerCase();

    const handleFinalize = async () => {
        setBusy(true);
        try {
            await apiFetch("/user/delete-account", {
                method: "POST",
                body: JSON.stringify({ confirmation_email: emailConfirm.trim() }),
            });
            await onDeleted();
        } catch (e) {
            toast.error(e.message || "Delete failed");
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={busy ? undefined : onClose}
        >
            <div
                data-testid="delete-account-modal"
                className="w-full max-w-md rounded-2xl border border-[var(--color-alert)]/40 bg-[var(--color-card)] p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="h-5 w-5 text-[var(--color-alert)]" />
                    <h2 className="text-lg font-semibold text-[var(--color-alert)]">
                        Delete account · Step {step} of 3
                    </h2>
                </div>

                {step === 1 && (
                    <div data-testid="delete-step-1" className="space-y-4">
                        <p className="text-sm text-text-secondary">
                            This action is <strong>permanent</strong> and cannot be
                            undone. It cancels your Stripe subscription (if any) and
                            deletes every record you own across all modules.
                        </p>
                        <label className="flex items-start gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="delete-ack-checkbox"
                                checked={acknowledged}
                                onChange={(e) => setAcknowledged(e.target.checked)}
                                className="mt-1"
                            />
                            <span>
                                I understand this action is permanent and cannot be
                                undone.
                            </span>
                        </label>
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={onClose}
                                className="text-xs px-3 py-2 text-text-secondary hover:text-text-main"
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="delete-step-1-next"
                                disabled={!canStep2}
                                onClick={() => setStep(2)}
                                className="text-xs rounded-full border border-[var(--color-alert)]/50 bg-[var(--color-alert)]/10 px-4 py-2 text-[var(--color-alert)] hover:bg-[var(--color-alert)]/20 disabled:opacity-50"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div data-testid="delete-step-2" className="space-y-4">
                        <p className="text-sm text-text-secondary">
                            Type <code className="text-[var(--color-alert)] font-mono">DELETE</code>{" "}
                            in the field to confirm.
                        </p>
                        <input
                            data-testid="delete-typed-confirm"
                            value={typedDelete}
                            onChange={(e) => setTypedDelete(e.target.value)}
                            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2 text-sm font-mono focus:border-[var(--color-alert)] focus:outline-none"
                            placeholder="DELETE"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={() => setStep(1)}
                                className="text-xs px-3 py-2 text-text-secondary hover:text-text-main"
                            >
                                Back
                            </button>
                            <button
                                data-testid="delete-step-2-next"
                                disabled={!canStep3}
                                onClick={() => setStep(3)}
                                className="text-xs rounded-full border border-[var(--color-alert)]/50 bg-[var(--color-alert)]/10 px-4 py-2 text-[var(--color-alert)] hover:bg-[var(--color-alert)]/20 disabled:opacity-50"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div data-testid="delete-step-3" className="space-y-4">
                        <p className="text-sm text-text-secondary">
                            Re-enter your email <strong>{userEmail}</strong> to finalize.
                        </p>
                        <input
                            data-testid="delete-email-confirm"
                            value={emailConfirm}
                            onChange={(e) => setEmailConfirm(e.target.value)}
                            type="email"
                            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2 text-sm focus:border-[var(--color-alert)] focus:outline-none"
                            placeholder="you@example.com"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={() => setStep(2)}
                                className="text-xs px-3 py-2 text-text-secondary hover:text-text-main"
                                disabled={busy}
                            >
                                Back
                            </button>
                            <button
                                data-testid="delete-confirm-final"
                                disabled={!canSubmit || busy}
                                onClick={handleFinalize}
                                className="text-xs rounded-full bg-[var(--color-alert)] text-white px-4 py-2 hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                            >
                                {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                                Delete permanently
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
